<template>
  <section class="px-3 py-4 sm:px-5 sm:py-6 lg:px-8">
    <div class="mx-auto max-w-7xl">
      <header class="flex items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-white px-4 py-4 shadow-sm sm:px-6">
        <div class="flex min-w-0 items-center gap-3">
          <HistoryBackButton />
          <div class="min-w-0">
            <h1 class="m-0 text-xl font-semibold text-slate-950 sm:text-2xl">Checkins</h1>
            <p class="m-0 mt-1 text-sm text-slate-500">Businesses you have checked in to.</p>
          </div>
        </div>
        <span class="shrink-0 rounded-full bg-blue-50 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.1em] text-blue-700">
          {{ checkins.length }} total
        </span>
      </header>

      <div v-if="loading" class="checkin-grid mt-5" aria-label="Loading checkins">
        <div v-for="index in 6" :key="index" class="grid animate-pulse gap-3 rounded-xl border border-slate-200 bg-white p-3 shadow-sm" aria-hidden="true">
          <div class="flex items-center gap-2.5 rounded-xl bg-slate-50 p-2.5">
            <span class="h-9 w-9 rounded-xl bg-slate-200"></span>
            <span class="grid flex-1 gap-2"><span class="h-3.5 w-2/3 rounded-full bg-slate-200"></span><span class="h-3 w-5/6 rounded-full bg-slate-100"></span></span>
          </div>
          <span class="h-3.5 rounded-full bg-slate-100"></span>
          <span class="h-3.5 w-4/5 rounded-full bg-slate-100"></span>
        </div>
      </div>

      <div v-else-if="error" class="mt-5 rounded-2xl bg-red-50 px-4 py-6 text-center text-sm font-semibold text-red-700">
        <p class="m-0">{{ error }}</p>
        <button class="mt-3 rounded-full bg-white px-4 py-2 text-xs font-semibold shadow-sm" type="button" @click="loadCheckins">Try again</button>
      </div>

      <div v-else-if="!checkins.length" class="mt-5 rounded-2xl border border-slate-200 bg-white py-12 text-center shadow-sm">
        <div class="mx-auto grid h-16 w-16 place-items-center rounded-2xl bg-blue-50 text-blue-600"><i class="ri-map-pin-user-line text-3xl" aria-hidden="true"></i></div>
        <p class="m-0 mt-4 text-lg font-semibold text-slate-900">No checkins posted</p>
        <p class="mt-2 text-sm text-slate-500">Your business checkins will appear here.</p>
      </div>

      <div v-else class="checkin-grid mt-5">
        <FlyerCard
          v-for="checkin in checkins"
          :key="checkin.id"
          :flyer="checkin"
          view="masonry"
          :show-author="false"
          :business-post-clickable="false"
          show-business-map-link
          collapsible-comment
        />
      </div>
    </div>
  </section>
</template>

<script setup>
import { onMounted, ref } from 'vue'
import api from '@/services/api'
import FlyerCard from '@/components/common/FlyerCard.vue'
import HistoryBackButton from '@/components/common/HistoryBackButton.vue'
import { useSessionStore } from '@/stores/session'

const session = useSessionStore()
const checkins = ref([])
const loading = ref(false)
const error = ref('')

const flattenGroups = (value) => value && typeof value === 'object'
  ? Object.values(value).flatMap((items) => Array.isArray(items) ? items : [])
  : []

const extractRows = (payload) => {
  if (Array.isArray(payload)) return payload
  const candidates = [payload?.list, payload?.data, payload?.result, payload?.checkins, payload?.checkin_list, payload?.business_checkins, payload?.data?.list, payload?.data?.checkins]
  return candidates.find(Array.isArray) || flattenGroups(candidates.find((value) => value && typeof value === 'object'))
}

const normalizeCheckin = (item = {}, index) => ({
  ...item,
  id: item.id || item.checkin_id || `checkin-${item.business_id || index}-${index}`,
  flyer_type: 'checkin',
  review_comment: item.comments ?? item.review_comment ?? '',
  business_name: item.s_business || item.business_name || '',
  business_adress: item.s_address || item.business_adress || item.business_address || '',
  business_lat: item.s_latit ?? item.business_lat ?? '',
  business_long: item.s_longit ?? item.business_long ?? '',
  created_at: item.checkin_date || item.created_at || ''
})

const loadCheckins = async () => {
  const userId = session.userId || session.user?.user_id || ''
  if (!userId) {
    error.value = 'Checkins could not be loaded.'
    return
  }
  loading.value = true
  error.value = ''
  try {
    const { data } = await api.listCheckins({ user_id: userId })
    checkins.value = extractRows(data).map(normalizeCheckin)
  } catch (requestError) {
    checkins.value = []
    error.value = requestError?.response?.data?.message || 'Checkins could not be loaded.'
  } finally {
    loading.value = false
  }
}

onMounted(loadCheckins)
</script>

<style scoped>
.checkin-grid {
  columns: 1;
  column-gap: 0.85rem;
}

.checkin-grid > * {
  break-inside: avoid;
  margin-bottom: 0.85rem;
}

@media (min-width: 1024px) {
  .checkin-grid {
    columns: 2;
  }
}

</style>
